import { useState } from 'react';
import { Link } from 'react-router';

import { errorMessage } from '@/api/client';
import { useComponents } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { configPath, useScopeParam } from '@/app/scope';
import { Button, LinkButton } from '@/components/Button';
import { Plus } from '@/components/icons';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import { EmptyState, ErrorNotice, Loading, Panel, Table, Td, Th } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { groupOf } from '@/domain/health';
import type { ComponentConfig, Scope } from '@/domain/types';

import { AddComponentDialog } from './AddComponentDialog';
import { formatLadder } from './helpers';
import { PageHeader } from './shared';

export function ConfigListPage() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <ConfigList scope={scope} />;
}

function ConfigList({ scope }: { scope: Scope }) {
  const components = useComponents(scope);
  const [adding, setAdding] = useState(false);
  // Mounted per use so its fields start blank; kept a moment after closing to fade out.
  const showDialog = useLingering(adding ? true : null, DIALOG_LINGER_MS);
  const configs: ComponentConfig[] = components.data?.components.map((c) => c.config) ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Configuration"
        actions={
          <>
            <LinkButton to={configPath(scope, '_scope')}>Scope templates</LinkButton>
            <Button variant="primary" icon={<Plus size={15} />} onClick={() => setAdding(true)}>
              Add component
            </Button>
          </>
        }
      />

      {components.isPending ? <Loading label="Loading configuration…" /> : null}
      {components.isError ? (
        <ErrorNotice
          title="The configuration could not be loaded"
          message={errorMessage(components.error)}
          action={
            <Button size="sm" onClick={() => void components.refetch()}>
              Try again
            </Button>
          }
        />
      ) : null}

      {components.data ? (
        <Panel bodyClassName="p-0">
          {configs.length === 0 ? (
            <EmptyState
              title="No components configured for this scope"
              action={
                <Button variant="primary" icon={<Plus size={15} />} onClick={() => setAdding(true)}>
                  Add component
                </Button>
              }
            />
          ) : (
            <Table className="[&>tbody>tr:last-child>td]:border-b-0">
              <thead>
                <tr>
                  <Th className="pl-5">Component</Th>
                  <Th className="text-right">Checks</Th>
                  <Th>Ladder</Th>
                  <Th>Recovery</Th>
                  <Th>Runner</Th>
                  <Th>Version</Th>
                  <Th className="pr-5">
                    <span className="sr-only">Actions</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => (
                  <ConfigRow key={config.capability_id} scope={scope} config={config} />
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      ) : null}

      {showDialog ? (
        <AddComponentDialog
          scope={scope}
          open={adding}
          onClose={() => setAdding(false)}
          existingIds={configs.map((c) => c.capability_id)}
        />
      ) : null}
    </div>
  );
}

function ConfigRow({ scope, config }: { scope: Scope; config: ComponentConfig }) {
  const group = groupOf(config.capability_id);
  return (
    <tr className="transition-tone hover:bg-surface-2">
      <Td className="pl-5">
        <div className="min-w-0">
          <Link
            to={configPath(scope, config.capability_id)}
            className="font-medium text-ink no-underline hover:underline"
          >
            {group ? <span className="text-ink-3 font-normal">{group} · </span> : null}
            {config.display_name}
          </Link>
          <div className="text-meta text-ink-3 flex flex-wrap gap-x-2">
            <code>{config.capability_id}</code>
            <span>
              from <code>{config.source}</code>
            </span>
          </div>
        </div>
      </Td>
      <Td className="text-right tabular">
        <span title={config.checks.join(', ')}>{config.checks.length}</span>
      </Td>
      <Td>
        {/* One line where it fits, a step per line where it does not, as the history page shows it. */}
        <span className="flex flex-wrap gap-x-2 gap-y-0.5">
          {formatLadder(config.ladder)
            .split(' · ')
            .map((step, index, steps) => (
              <span key={step} className="whitespace-nowrap">
                {step}
                {index < steps.length - 1 ? <span className="text-ink-3"> ·</span> : null}
              </span>
            ))}
        </span>
      </Td>
      <Td className="whitespace-nowrap">
        {config.recovery_threshold_runs} passing{' '}
        {config.recovery_threshold_runs === 1 ? 'run' : 'runs'}
        <span className="text-ink-3">
          {' '}
          · {config.auto_recovery ? 'automatic' : 'a person confirms'}
        </span>
      </Td>
      <Td className="whitespace-nowrap">
        every {config.runner_cadence_minutes} min
        <span className="text-ink-3">
          {' '}
          · blind after {config.staleness_tolerance_cycles} missed
        </span>
      </Td>
      <Td className="whitespace-nowrap">
        <span className="tabular">v{config.version}</span>
        <div className="text-meta text-ink-3">
          <Timestamp iso={config.updated_at} mode="auto" /> · {config.updated_by}
        </div>
      </Td>
      <Td className="whitespace-nowrap text-right pr-5">
        <Link to={configPath(scope, config.capability_id)}>Edit</Link>
        <span className="text-ink-3"> · </span>
        <Link to={configPath(scope, config.capability_id, 'history')}>History</Link>
      </Td>
    </tr>
  );
}
